import React from 'react';
import momenttz from 'moment-timezone';
import ReactHTMLTableToExcel from 'react-html-table-to-excel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHomeAlt,
} from '@fortawesome/free-solid-svg-icons';
import {
  Box,
  Breadcrumbs,
  Button,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';

import { ILayoutPage } from 'Module/Layout/Interface';
import { IAssociatesReport } from 'Module/AssociatesReport/Interface';
import { APURACAO, HOME } from 'Shared/Constant/Routes';
import convertToNumber from 'Shared/Utils/ConvertToNumber';

interface IApuracaoPage extends ILayoutPage {
  showLoadingThisPage: boolean;
  listApuracao: [IAssociatesReport?];
  toggleGetApuracao(): unknown;
}

function ApuracaoPage(props: IApuracaoPage) {
  const parseNumber = React.useCallback((value: any): number => {
    if (value == null) {
      return 0;
    }
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }
    return convertToNumber(String(value));
  }, []);

  const formatPercent = React.useCallback((value: number): string => {
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, []);

  const formatCurrency = React.useCallback((value: number): string => {
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, []);

  const formatDocumento = React.useCallback((documento?: string): string => {
    const value = documento || '';
    if (!value) {
      return '';
    }
    return value.length <= 11 ? `CPF: ${value}` : `CNPJ: ${value}`;
  }, []);

  const rows = React.useMemo(() => {
    return (props.listApuracao || [])
      .filter((item) => Boolean(item))
      .map((item, index) => {
        const mix = parseNumber(item?.mixPrincipal || item?.mix || '0');
        const metaMix = parseNumber(item?.metaMixPrincipal || item?.metaMix || '0');
        const percentMix = metaMix > 0 ? (mix / metaMix) * 100 : 0;
        const faturamento = parseNumber(item?.faturamento || '0');
        const metaFaturamento = parseNumber(item?.metaFaturamento || '0');
        const percentFat = metaFaturamento > 0 ? (faturamento / metaFaturamento) * 100 : 0;

        return {
          sequ: index + 1,
          codigo: item?.codigo || '',
          nome: item?.nome || '',
          documento: formatDocumento(item?.documento),
          mix,
          metaMix,
          percentMix,
          faturamento,
          metaFaturamento,
          percentFat,
        };
      });
  }, [formatDocumento, parseNumber, props.listApuracao]);

  const totalFaturamento = React.useMemo(() => {
    return rows.reduce((acc, item) => acc + item.faturamento, 0);
  }, [rows]);

  const totalMetaFaturamento = React.useMemo(() => {
    return rows.reduce((acc, item) => acc + item.metaFaturamento, 0);
  }, [rows]);

  const totalPercent = React.useMemo(() => {
    return totalMetaFaturamento > 0 ? (totalFaturamento / totalMetaFaturamento) * 100 : 0;
  }, [totalFaturamento, totalMetaFaturamento]);

  return (
    <Box id="Apuracao" key="Apuracao" className="ChildrenRoot">
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Typography component="div" align="left">
            <Box id="BreadcrumbsBox" className="Breadcrumbs">
              <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" to={HOME}>
                  <FontAwesomeIcon icon={faHomeAlt} />
                </Link>
                <Link color="inherit" to={APURACAO}>
                  Apuração
                </Link>
              </Breadcrumbs>
            </Box>
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Button
            variant="contained"
            color="success"
            className="ButtonPrimary"
            type="button"
            onClick={props.toggleGetApuracao}
          >
            Gerar relatório
          </Button>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography align="right">
            <ReactHTMLTableToExcel
              id="apuracao-export-xls"
              className="download-table-xls-button"
              table="table-apuracao-xls"
              filename={`${momenttz().tz('America/Sao_Paulo').format('YYYY-MM-DD')}-Apuracao`}
              sheet="Apuracao"
              buttonText="Exportar arquivo XLS"
            />
          </Typography>
        </Grid>

        <Grid item xs={12}>
          {props.showLoadingThisPage && <LinearProgress />}
        </Grid>

        <Grid item xs={12}>
          <TableContainer component={Paper} sx={{ border: '1px solid #1a1a1a' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    colSpan={10}
                    sx={{
                      backgroundColor: '#8cc152',
                      color: '#000',
                      fontWeight: 700,
                      border: '1px solid #1a1a1a',
                      textAlign: 'center',
                      py: 0.8,
                    }}
                  >
                    APURAÇÃO ATINGIMENTO META CAMPANHA FINAL DE ANO 2025/26 &quot;COMPRA PREMIADA REDEMAIS&quot;
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ backgroundColor: '#d9d9d9', fontWeight: 700, border: '1px solid #1a1a1a' }}>SEQU</TableCell>
                  <TableCell sx={{ backgroundColor: '#d9d9d9', fontWeight: 700, border: '1px solid #1a1a1a' }}>Código</TableCell>
                  <TableCell sx={{ backgroundColor: '#d9d9d9', fontWeight: 700, border: '1px solid #1a1a1a' }}>Nome</TableCell>
                  <TableCell sx={{ backgroundColor: '#d9d9d9', fontWeight: 700, border: '1px solid #1a1a1a' }}>CPF/CNPJ</TableCell>
                  <TableCell sx={{ backgroundColor: '#d9d9d9', fontWeight: 700, border: '1px solid #1a1a1a', textAlign: 'right' }}>Mix</TableCell>
                  <TableCell sx={{ backgroundColor: '#d9d9d9', fontWeight: 700, border: '1px solid #1a1a1a', textAlign: 'right' }}>Meta Mix</TableCell>
                  <TableCell sx={{ backgroundColor: '#d9d9d9', fontWeight: 700, border: '1px solid #1a1a1a', textAlign: 'right' }}>% Mix/Meta</TableCell>
                  <TableCell sx={{ backgroundColor: '#d9d9d9', fontWeight: 700, border: '1px solid #1a1a1a', textAlign: 'right' }}>Faturamento</TableCell>
                  <TableCell sx={{ backgroundColor: '#d9d9d9', fontWeight: 700, border: '1px solid #1a1a1a', textAlign: 'right' }}>Meta Faturamento</TableCell>
                  <TableCell sx={{ backgroundColor: '#d9d9d9', fontWeight: 700, border: '1px solid #1a1a1a', textAlign: 'right' }}>% Fat/Meta</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={`${row.sequ}-${row.codigo}`}>
                    <TableCell sx={{ border: '1px solid #1a1a1a' }}>{row.sequ}</TableCell>
                    <TableCell sx={{ border: '1px solid #1a1a1a' }}>{row.codigo}</TableCell>
                    <TableCell sx={{ border: '1px solid #1a1a1a' }}>{row.nome}</TableCell>
                    <TableCell sx={{ border: '1px solid #1a1a1a' }}>{row.documento}</TableCell>
                    <TableCell sx={{ border: '1px solid #1a1a1a', textAlign: 'right' }}>
                      {row.mix.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </TableCell>
                    <TableCell sx={{ border: '1px solid #1a1a1a', textAlign: 'right' }}>
                      {row.metaMix.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </TableCell>
                    <TableCell sx={{ border: '1px solid #1a1a1a', textAlign: 'right' }}>{formatPercent(row.percentMix)}%</TableCell>
                    <TableCell sx={{ border: '1px solid #1a1a1a', textAlign: 'right' }}>R$ {formatCurrency(row.faturamento)}</TableCell>
                    <TableCell sx={{ border: '1px solid #1a1a1a', textAlign: 'right' }}>R$ {formatCurrency(row.metaFaturamento)}</TableCell>
                    <TableCell sx={{ border: '1px solid #1a1a1a', textAlign: 'right' }}>{formatPercent(row.percentFat)}%</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: '1px solid #1a1a1a', fontWeight: 700, textAlign: 'center', py: 1 }}>
                    TOTAL FATURAMENTO X META DE FATURAMENTO
                  </TableCell>
                  <TableCell sx={{ border: '1px solid #1a1a1a', fontWeight: 700, textAlign: 'right' }}>R$ {formatCurrency(totalFaturamento)}</TableCell>
                  <TableCell sx={{ border: '1px solid #1a1a1a', fontWeight: 700, textAlign: 'right' }}>R$ {formatCurrency(totalMetaFaturamento)}</TableCell>
                  <TableCell sx={{ border: '1px solid #1a1a1a', fontWeight: 700, textAlign: 'right' }}>{formatPercent(totalPercent)}%</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid item xs={12} sx={{ display: 'none' }}>
          <table id="table-apuracao-xls" style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th
                  colSpan={10}
                  style={{
                    backgroundColor: '#8cc152',
                    color: '#000000',
                    fontWeight: 700,
                    border: '1px solid #1a1a1a',
                    textAlign: 'center',
                    padding: '6px',
                  }}
                >
                  APURAÇÃO ATINGIMENTO META CAMPANHA FINAL DE ANO 2025/26 "COMPRA PREMIADA "REDEMAIS"
                </th>
              </tr>
              <tr>
                <th style={{ backgroundColor: '#d9d9d9', fontWeight: 700, border: '1px solid #1a1a1a' }}>SEQU</th>
                <th style={{ backgroundColor: '#d9d9d9', fontWeight: 700, border: '1px solid #1a1a1a' }}>Código</th>
                <th style={{ backgroundColor: '#d9d9d9', fontWeight: 700, border: '1px solid #1a1a1a' }}>Nome</th>
                <th style={{ backgroundColor: '#d9d9d9', fontWeight: 700, border: '1px solid #1a1a1a' }}>CPF/CNPJ</th>
                <th style={{ backgroundColor: '#d9d9d9', fontWeight: 700, border: '1px solid #1a1a1a', textAlign: 'right' }}>Mix</th>
                <th style={{ backgroundColor: '#d9d9d9', fontWeight: 700, border: '1px solid #1a1a1a', textAlign: 'right' }}>Meta Mix</th>
                <th style={{ backgroundColor: '#d9d9d9', fontWeight: 700, border: '1px solid #1a1a1a', textAlign: 'right' }}>% Mix/Meta</th>
                <th style={{ backgroundColor: '#d9d9d9', fontWeight: 700, border: '1px solid #1a1a1a', textAlign: 'right' }}>Faturamento</th>
                <th style={{ backgroundColor: '#d9d9d9', fontWeight: 700, border: '1px solid #1a1a1a', textAlign: 'right' }}>Meta Faturamento</th>
                <th style={{ backgroundColor: '#d9d9d9', fontWeight: 700, border: '1px solid #1a1a1a', textAlign: 'right' }}>% Fat/Meta</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`xls-${row.sequ}-${row.codigo}`}>
                  <td style={{ border: '1px solid #1a1a1a' }}>{row.sequ}</td>
                  <td style={{ border: '1px solid #1a1a1a' }}>{row.codigo}</td>
                  <td style={{ border: '1px solid #1a1a1a' }}>{row.nome}</td>
                  <td style={{ border: '1px solid #1a1a1a' }}>{row.documento}</td>
                  <td style={{ border: '1px solid #1a1a1a', textAlign: 'right' }}>
                    {row.mix.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </td>
                  <td style={{ border: '1px solid #1a1a1a', textAlign: 'right' }}>
                    {row.metaMix.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </td>
                  <td style={{ border: '1px solid #1a1a1a', textAlign: 'right' }}>{formatPercent(row.percentMix)}%</td>
                  <td style={{ border: '1px solid #1a1a1a', textAlign: 'right' }}>R$ {formatCurrency(row.faturamento)}</td>
                  <td style={{ border: '1px solid #1a1a1a', textAlign: 'right' }}>R$ {formatCurrency(row.metaFaturamento)}</td>
                  <td style={{ border: '1px solid #1a1a1a', textAlign: 'right' }}>{formatPercent(row.percentFat)}%</td>
                </tr>
              ))}
              <tr>
                <td colSpan={7} style={{ border: '1px solid #1a1a1a', fontWeight: 700, textAlign: 'center', padding: '8px' }}>
                  TOTAL FATURAMENTO X META DE FATURAMENTO
                </td>
                <td style={{ border: '1px solid #1a1a1a', fontWeight: 700, textAlign: 'right' }}>R$ {formatCurrency(totalFaturamento)}</td>
                <td style={{ border: '1px solid #1a1a1a', fontWeight: 700, textAlign: 'right' }}>R$ {formatCurrency(totalMetaFaturamento)}</td>
                <td style={{ border: '1px solid #1a1a1a', fontWeight: 700, textAlign: 'right' }}>{formatPercent(totalPercent)}%</td>
              </tr>
            </tbody>
          </table>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ApuracaoPage;
